import React from "react";
import ReactDOM from "react-dom/client";
import { delay, put, select, takeEvery } from "redux-saga/effects";

import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import {
  AuthActions,
  authActions,
  getIsUserSignedIn,
  getUser,
  NavigationActions,
  UserInfo,
} from "@gamedev-web/gamedev-sdk-core";
import { GameDevAuthMeControl, GameDevAuthProvider } from "@gaming/gdas-react";

import { LoginButton } from "../../components/LoginButton/LoginButton";

const siteLocationPrefix = "/games/publish";
const signInUrl = `${siteLocationPrefix}/auth/login`;
const signOutUrl = `${siteLocationPrefix}/auth/logout`;

type WindowType = {
  msCommonShell: {
    load: (_props: Record<string, unknown>) => void;
  };
};

let testEnv = false;
export const setTestEnv = (value: boolean) => {
  testEnv = value;
};

export function* populateUserInfo(action: ReturnType<typeof authActions.checkAuthStatus>) {
  const loggedIn: boolean | undefined = yield select(getIsUserSignedIn);
  if (loggedIn && !action.initialClientSetup) {
    return;
  }

  const browserWindow: WindowType = window as unknown as WindowType;

  try {
    yield delay(500);
    const userInfo: UserInfo = yield select(getUser);

    const uhfContainer = document.querySelector(".c-uhfh-actions");
    if (!uhfContainer) {
      return;
    }

    const existingControl = uhfContainer.querySelector("#meControl");
    if (existingControl) {
      // If the control already exists, we don't need to re-render it.
      return;
    }

    const containerDiv = document.createElement("div");
    containerDiv.setAttribute("id", "meControl");
    containerDiv.setAttribute("class", "gdas-root");

    const root = ReactDOM.createRoot(uhfContainer.appendChild(containerDiv));
    if (userInfo) {
      if (testEnv || browserWindow /* .msCommonShell */) {
        if (!testEnv) {
          root.render(
            React.createElement(
              FluentProvider,
              { theme: webLightTheme },
              React.createElement(
                GameDevAuthProvider,
                {
                  config: {
                    clientId: "b43d62f4-3cfb-433d-8e28-69da90f843cf",
                    partnerId: "playfab",
                    account: {
                      username: userInfo.emailAddress,
                      name: userInfo.displayName as string,
                      picture: userInfo.avatarImage,
                      tenantId: "",
                    },
                  },
                },
                React.createElement(GameDevAuthMeControl, {
                  onSignIn: () => (window.location.href = signInUrl),
                  onSignOut: () => (window.location.href = signOutUrl),
                  onSwitchTo: () => (window.location.href = signInUrl),
                  commands: [
                    // This removes the Switch Directory button from the login menu.
                    {
                      text: "",
                      onClick: () => {},
                    },
                  ],
                })
              )
            )
          );
        }
      }
      yield put(authActions.updateUser(userInfo));
    } else {
      root.render(
        React.createElement(LoginButton, { onSignIn: () => (window.location.href = signInUrl) })
      );
      yield put(authActions.updateUser(undefined));
    }
  } catch (e) {
    // TODO: telemetry and error handling.
    yield put(authActions.updateUser(undefined));
  }
}

export function* authSagas() {
  yield takeEvery(AuthActions.CHECK_AUTH_STATUS, populateUserInfo);
  yield takeEvery(NavigationActions.LOCATION_CHANGE, populateUserInfo);
  // We need to do the initial auth status check. After that, location changes within the
  // redux-router will check it.
  yield put({ type: AuthActions.CHECK_AUTH_STATUS, initialClientSetup: true });
}
